import { useEffect } from "react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { FAQ } from "../data/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { ArrowRight } from "lucide-react";

const FAQ_SCHEMA_ID = "faq-schema-jsonld";

export default function Faq() {
  useSeo(
    "Domande frequenti (FAQ) — Psicologa e Psicoterapeuta a Macerata",
    "Domande frequenti sulla psicoterapia a Macerata: primo colloquio, durata del percorso, EMDR, riservatezza, psicoterapia online e sede dello studio."
  );

  // Dati strutturati FAQPage per la SEO.
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a.join(" ") },
      })),
    };
    let script = document.getElementById(FAQ_SCHEMA_ID);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = FAQ_SCHEMA_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      const s = document.getElementById(FAQ_SCHEMA_ID);
      if (s) s.remove();
    };
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Domande frequenti"
        title="Le domande più comuni"
        subtitle="Alcune risposte per orientarti prima di un primo contatto. Per qualsiasi dubbio non esitare a scrivermi: valuteremo insieme la tua situazione."
      />

      <section className="py-16 md:py-24" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-ink hover:no-underline py-6 [&>svg]:text-sage-dark [&>svg]:h-5 [&>svg]:w-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">
                    <div className="space-y-4 text-stone leading-relaxed text-base md:text-lg max-w-3xl">
                      {f.a.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal>
            <div className="mt-16 bg-sand border border-border rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-ink">Hai altre domande?</h2>
                <p className="text-stone mt-3 max-w-xl leading-relaxed">
                  Scrivimi due righe sul motivo del contatto: ti risponderò e valuteremo insieme come procedere.
                </p>
              </div>
              <PillButton to="/contatti" testid="faq-cta">
                Richiedi informazioni <ArrowRight size={18} />
              </PillButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
